import { TagsManagerView } from '../view/TagsManagerView';
import { templateTagsManager } from '../view/template';
import { TagsManagerRepository } from '../repository/TagsManagerRepository';
import {
  BaseWebComponentController,
  IListenerConfig,
  WebComponentEvent,
} from '../../../framework/BaseWebComponentController';
import { TagsManagerWebComponent } from '..';
import { OnDeleteTag, OnTagNameClick, TagsManagerGridEvents } from '../../tagsManagerGrid/events/events';
import { GzDialogWebComponent } from '../../../ui/gzDialog';
import { TagPanelWebComponent } from '../../tagPanel';
import { showCancelWarningDialog, showToast } from '../../helpers/commonFunctions';
import { TagPanelEvents } from '../../common/events/TagPanelEvents';
import { GzButtonWebComponent } from '../../../ui/gzButton';
import { ToastSeverity } from '../../../ui/gzToastNotification/type';
import { TagActionContext } from '../../common/types/TagActionContext';
import { ConsoleRequest, CreateRequestPayload, FilterKeyMapper } from '../../../utils/request';
import { translateFn } from '../../../utils/translator.helper';

export class TagsManagerController extends BaseWebComponentController {
  public component: TagsManagerWebComponent;
  public dataRepository: TagsManagerRepository;
  public view: TagsManagerView;
  public viewConstructor: typeof TagsManagerView;
  public dataRepositoryConstructor: typeof TagsManagerRepository;

  private createTagDialog: GzDialogWebComponent;
  private editTagDialog: GzDialogWebComponent;
  private cloneTagDialog: GzDialogWebComponent;

  constructor(component: TagsManagerWebComponent) {
    super(component);
    this.initialSetup({
      viewConstructor: TagsManagerView,
      viewTemplate: templateTagsManager,
      dataRepositoryConstructor: TagsManagerRepository,
    });
  }

  public computeEventListeners(): IListenerConfig[] {
    return [
      {
        element: this.view.dom.$tagsManagerGrid,
        event: TagsManagerGridEvents.OnAddTag,
        callback: this.showCreateTag,
      },
      {
        element: this.view.dom.$tagsManagerGrid,
        event: TagsManagerGridEvents.OnDeleteTag,
        callback: this.showDeleteTagConfirmationWindow,
      },
      {
        element: this.view.dom.$tagsManagerGrid,
        event: TagsManagerGridEvents.OnTagNameClick,
        callback: (event: OnTagNameClick) => {
          this.showEditTag(event.detail.tagId, event.detail.tagName);
        },
      },
      {
        element: this.view.dom.$tagsManagerGrid,
        event: TagsManagerGridEvents.OnDeleteTagFromInlineButton,
        callback: this.onDeleteTagFromInlineButton,
      },
      {
        element: this.view.dom.$tagsManagerGrid,
        event: TagsManagerGridEvents.OnCloneTag,
        callback: this.showCloneTag,
      },
    ];
  }

  async getDataForView() {
    return {};
  }

  public async showEditTag(tagId: string, tagName: string) {
    const editTagPanel = new TagPanelWebComponent();
    editTagPanel.setAttribute('context', TagActionContext.Edit);
    editTagPanel.setAttribute('editedTagId', tagId);

    const headline = this.dependencies.translateFn('Edit tag {tagName}', { tagName });

    const onMaybeCloseHandler = this.maybeCloseEditTagPanel.bind(this, editTagPanel);
    const dialogConfig = {
      headline: headline,
      bodyContent: editTagPanel,
      isOpen: true,
      isWindow: true,
      closableOutsideBox: false,
      onCloseHandler: onMaybeCloseHandler,
    };

    this.editTagDialog = GzDialogWebComponent.buildDialogElement(dialogConfig, this.view.dom.$wrapper);

    editTagPanel.addEventListener(TagPanelEvents.Close, this.closeEditTagWindow.bind(this));
    editTagPanel.addEventListener(TagPanelEvents.MaybeClose, onMaybeCloseHandler);
    editTagPanel.injectDependencies(this.dependencies);

    await editTagPanel.start();
  }

  private async showCreateTag() {
    const createTagPanel = new TagPanelWebComponent();
    createTagPanel.setAttribute('context', TagActionContext.Create);

    const dialogConfig = {
      headline: this.dependencies.translateFn('Create tag'),
      bodyContent: createTagPanel,
      isOpen: true,
      isWindow: true,
      closableOutsideBox: false,
      onCloseHandler: showCancelWarningDialog.bind(this, this.closeCreateTagWindow.bind(this)),
    };

    this.createTagDialog = GzDialogWebComponent.buildDialogElement(dialogConfig, this.view.dom.$wrapper);

    createTagPanel.addEventListener(TagPanelEvents.Close, this.closeCreateTagWindow.bind(this));
    createTagPanel.addEventListener(
      TagPanelEvents.MaybeClose,
      showCancelWarningDialog.bind(this, this.closeCreateTagWindow.bind(this))
    );
    createTagPanel.injectDependencies(this.dependencies);

    await createTagPanel.start();
  }

  private async showCloneTag(event: WebComponentEvent) {
    const cloneTagPanel = new TagPanelWebComponent();
    cloneTagPanel.setAttribute('context', TagActionContext.Clone);
    cloneTagPanel.setAttribute('clonedTagId', event.detail.rowData.item._id);
    cloneTagPanel.setAttribute('clonedTagName', event.detail.rowData.item.name);
    cloneTagPanel.setAttribute('clonedTagCompanyName', event.detail.rowData.item.company.name);

    const dialogConfig = {
      headline: this.dependencies.translateFn('Create tag'),
      bodyContent: cloneTagPanel,
      isOpen: true,
      isWindow: true,
      closableOutsideBox: false,
      onCloseHandler: showCancelWarningDialog.bind(this, this.closeCloneTagWindow.bind(this)),
    };

    this.cloneTagDialog = GzDialogWebComponent.buildDialogElement(dialogConfig, this.view.dom.$wrapper);

    cloneTagPanel.addEventListener(TagPanelEvents.Close, this.closeCloneTagWindow.bind(this));
    cloneTagPanel.addEventListener(
      TagPanelEvents.MaybeClose,
      showCancelWarningDialog.bind(this, this.closeCloneTagWindow.bind(this))
    );
    cloneTagPanel.injectDependencies(this.dependencies);

    await cloneTagPanel.start();
  }

  private maybeCloseEditTagPanel(editTagPanel: TagPanelWebComponent) {
    if (editTagPanel.hasUnsavedChanges) {
      showCancelWarningDialog.call(this, () => {
        this.closeEditTagWindow();
      });
    } else {
      this.closeEditTagWindow();
    }
  }

  private closeCreateTagWindow() {
    if (this.createTagDialog === null) {
      return;
    }

    this.createTagDialog.dismiss();
    this.view.dom.$tagsManagerGrid.reload(true, false);
  }

  private closeEditTagWindow() {
    if (this.editTagDialog === null) {
      return;
    }

    this.editTagDialog.dismiss();
    this.view.dom.$tagsManagerGrid.reload(true, false);
  }

  private closeCloneTagWindow() {
    if (this.cloneTagDialog === null) {
      return;
    }

    this.cloneTagDialog.dismiss();
    this.view.dom.$tagsManagerGrid.reload(true, false);
  }

  private showDeleteTagConfirmationWindow(event: OnDeleteTag) {
    const gzTagIds = event.detail.gzTagIds;
    const allSelected = event.detail.selectAllEnabled;
    const expectedCount = event.detail.expectedCount;
    const tagsCount = allSelected ? expectedCount : gzTagIds.length;
    const bodyContentHtml = `
    <p>
      ${tagsCount === 1
        ? translateFn('You are about to delete 1 tag.')
        : translateFn('You are about to delete {tagsCount} tags.', { tagsCount: tagsCount })
      }
      ${tagsCount === 1
        ? translateFn(
              'Any policy assignment rules or custom rules that are currently using the selected tag will be affected.')
        : translateFn(
              'Any policy assignment rules or custom rules that are currently using the selected tags will be affected.')
      }
    </p>`;

    const windowFooterHtml = `<gz-button id='confirm' type='outline' color='negative' class='mr-2'>
        ${translateFn('Delete')}
      </gz-button>
      <gz-button id='cancel' type='outline' color='secondary'>
        ${translateFn('Cancel')}
      </gz-button>`;

    const windowTitle = tagsCount === 1
                        ? translateFn('Delete tag?')
                        : translateFn('Delete tags?');

    const dialog = GzDialogWebComponent.computeCustomDialog(
      bodyContentHtml,
      windowFooterHtml,
      windowTitle,
      null,
      async () => {
        const confirmButton = dialog.shadowRoot?.querySelector<GzButtonWebComponent>('#confirm');
        if (confirmButton) {
          confirmButton.showSpinner = true;
          confirmButton.disabled = true;
        }

        const payload = allSelected ? this.getPayloadForFilteredRemove(expectedCount) : { records: gzTagIds };
        await this.removeSelectedGzTagRecordsHandler(
          payload,
          this.view.dom.$tagsManagerGrid.controller?.view.dom.$grid.isAllSelected
        );
        dialog.close();
        await this.view.dom.$tagsManagerGrid.reload(true, true);
      },
      null,
      false
    );
  }

  private onDeleteTagFromInlineButton(event: WebComponentEvent) {
    const selectedGzTagsIds = [{ _id: event.detail.rowData.item._id }];
    this.view.dom.$tagsManagerGrid.dispatchEvent(
      new OnDeleteTag(
        selectedGzTagsIds,
        false,
        this.view.dom.$tagsManagerGrid.controller?.view.dom.$grid?.controller?.view.dom.$gridFooter.numTotalItems || 0
      )
    );
  }

  private getPayloadForFilteredRemove(expectedCount: number) {
    const gridComponent = this.view.dom.$tagsManagerGrid.controller?.view.dom.$grid;
    const payload = ConsoleRequest.mapGridRequestToPayload(
      {
        columns: gridComponent.columns,
        filters: gridComponent.filters,
        extraParams: gridComponent.extraParams || {},
        pageNumber: gridComponent.pageNumber,
        pageSize: gridComponent.pageSize,
        sortColumnName: gridComponent.sortColumnName,
        sortDirection: gridComponent.sortDirection,
      },
      this.dependencies.getTimezone(),
      {
        'company.name': 'company.id',
      } as FilterKeyMapper
    );

    return {
      filter: payload['filter'],
      expectedCount: expectedCount,
    };
  }

  private async removeSelectedGzTagRecordsHandler(payload: CreateRequestPayload['payload'], allSelected: boolean) {
    try {
      await this.dataRepository.removeGzTagRecords(payload, allSelected);
      showToast(ToastSeverity.SUCCESS, this.dependencies.translateFn('Tags deleted successfully.'));
    } catch (error) {
      if (error.err?.exceptionType === 'Exceptions\\GzTagsExpectedCountException') {
        showToast(ToastSeverity.ERROR, error.err.message);
      } else {
        showToast(ToastSeverity.ERROR, this.dependencies.translateFn('Failed to delete tags.'));
      }
    }
  }
}
